import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import type { GrantStatementDraft, StepErrors } from '../types';

const ACTION_REQUIRED = 'Enter an action, e.g. storage:read.';
const RESOURCE_REQUIRED = 'Enter a resource, e.g. storage:files.';

export interface PolicyStatementBuilderProps {
  statements: GrantStatementDraft[];
  onChange: (next: GrantStatementDraft[]) => void;
  /** Field-keyed errors (`${index}.action` / `${index}.resource`) — frame-owned. */
  errors: StepErrors;
  onErrors: (next: StepErrors) => void;
}

/**
 * attach-policy rows (design §13.1): `Effect | Action | Resource` grid, mono
 * inputs, ✕ ghost remove (aria-label "Remove grant", 40px hit area). Rows are
 * free-form on purpose — the platform grammar is action/resource strings and
 * validation falls to the backend, surfaced verbatim (§13.2, T6 guard); blur
 * only catches the required pair members.
 */
export function PolicyStatementBuilder({
  statements,
  onChange,
  errors,
  onErrors,
}: PolicyStatementBuilderProps) {
  const setError = (key: string, message?: string) => {
    const next = { ...errors };
    if (message) {
      next[key] = message;
    } else {
      delete next[key];
    }
    onErrors(next);
  };

  const updateRow = (index: number, patch: Partial<GrantStatementDraft>) => {
    onChange(statements.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    onChange([...statements, { effect: 'Allow', action: '', resource: '' }]);
  };

  const removeRow = (index: number) => {
    onChange(statements.filter((_, i) => i !== index));
    // Drop stale error keys for the removed row and re-index the rest.
    const nextErrors: StepErrors = {};
    for (const [key, message] of Object.entries(errors)) {
      const [rawIndex, field] = key.split('.');
      const rowIndex = Number(rawIndex);
      if (Number.isNaN(rowIndex)) {
        nextErrors[key] = message;
      } else if (rowIndex !== index) {
        nextErrors[`${rowIndex > index ? rowIndex - 1 : rowIndex}.${field}`] = message;
      }
    }
    onErrors(nextErrors);
  };

  return (
    <div className="space-y-3">
      {statements.length > 0 && (
        <div
          className="hidden gap-2 text-sm text-muted-foreground md:grid md:grid-cols-[110px_1fr_1fr_36px]"
          aria-hidden="true"
        >
          <span>Effect</span>
          <span>Action</span>
          <span>Resource</span>
          <span />
        </div>
      )}

      <div className="space-y-2">
        {statements.map((statement, index) => {
          const actionErrorKey = `${index}.action`;
          const resourceErrorKey = `${index}.resource`;
          const actionError = errors[actionErrorKey];
          const resourceError = errors[resourceErrorKey];
          return (
            <div key={index} className="space-y-1">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[110px_1fr_1fr_36px]">
                <Select
                  value={statement.effect}
                  onValueChange={(effect) =>
                    updateRow(index, { effect: effect as GrantStatementDraft['effect'] })
                  }
                  aria-label={`Effect for grant ${index + 1}`}
                >
                  <SelectTrigger className="h-9 w-full text-sm" aria-hidden="true">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Allow">Allow</SelectItem>
                    <SelectItem value="Deny">Deny</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={statement.action}
                  onChange={(event) => {
                    updateRow(index, { action: event.target.value });
                    if (event.target.value.trim() !== '') {
                      setError(actionErrorKey);
                    }
                  }}
                  onBlur={(event) =>
                    setError(
                      actionErrorKey,
                      event.target.value.trim() === '' ? ACTION_REQUIRED : undefined,
                    )
                  }
                  placeholder="e.g. storage:read"
                  aria-label={`Action for grant ${index + 1}`}
                  className="font-mono text-sm"
                  aria-invalid={actionError ? true : undefined}
                  aria-describedby={actionError ? `${actionErrorKey}-error` : undefined}
                />
                <Input
                  value={statement.resource}
                  onChange={(event) => {
                    updateRow(index, { resource: event.target.value });
                    if (event.target.value.trim() !== '') {
                      setError(resourceErrorKey);
                    }
                  }}
                  onBlur={(event) =>
                    setError(
                      resourceErrorKey,
                      event.target.value.trim() === '' ? RESOURCE_REQUIRED : undefined,
                    )
                  }
                  placeholder="e.g. storage:files"
                  aria-label={`Resource for grant ${index + 1}`}
                  className="font-mono text-sm"
                  aria-invalid={resourceError ? true : undefined}
                  aria-describedby={resourceError ? `${resourceErrorKey}-error` : undefined}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-full shrink-0 md:w-10"
                  aria-label="Remove grant"
                  onClick={() => removeRow(index)}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              {actionError ? (
                <p id={`${actionErrorKey}-error`} className="text-sm text-destructive">
                  {actionError}
                </p>
              ) : null}
              {resourceError ? (
                <p id={`${resourceErrorKey}-error`} className="text-sm text-destructive">
                  {resourceError}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      <Button type="button" variant="secondary" size="sm" onClick={addRow}>
        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
        Add grant
      </Button>

      {statements.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No additional policies yet. Only add what this agent actually needs.
        </p>
      )}
    </div>
  );
}
